import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Shell = ({ children, testId }) => (
  <div className="min-h-screen bg-[#F9F9F6] grid place-items-center p-6" data-testid={testId}>
    <div className="w-full max-w-md">
      <div className="mb-8 flex justify-center"><Logo /></div>
      <div className="rounded-3xl border bg-white p-8 text-center fade-up">{children}</div>
    </div>
  </div>
);

export const VerifyEmail = () => {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [err, setErr] = useState(null);
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(({ data }) => { setUser(data); toast.success("Email confermata, benvenuto!"); navigate("/app", { replace: true }); })
      .catch((e) => setErr(errMsg(e)));
  }, [params, setUser, navigate]);
  return (
    <Shell testId="verify-email-page">
      {err ? <XCircle className="h-12 w-12 mx-auto text-red-500" /> : <Loader2 className="h-12 w-12 mx-auto animate-spin text-[#1E3A8A]" />}
      <h1 className="mt-4 text-2xl font-semibold" data-testid="verify-email-title">{err ? "Conferma non riuscita" : "Stiamo confermando la tua email…"}</h1>
      {err && <p className="mt-2 text-slate-600 text-sm" data-testid="verify-email-error">{err}</p>}
      {err && <Button asChild className="mt-6 rounded-full" data-testid="verify-email-back"><Link to="/accedi">Vai all'accesso</Link></Button>}
    </Shell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error("Le due password non coincidono");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw.a });
      setUser(data);
      toast.success("Password aggiornata");
      navigate("/app", { replace: true });
    } catch (e2) {
      toast.error(errMsg(e2));
      setBusy(false);
    }
  };
  return (
    <Shell testId="reset-password-page">
      <CheckCircle2 className="h-10 w-10 mx-auto text-[#1E3A8A]" />
      <h1 className="mt-3 text-2xl font-semibold">Scegli una nuova password</h1>
      <form onSubmit={submit} className="mt-6 space-y-4 text-left">
        <div className="space-y-1.5"><Label htmlFor="pw1">Nuova password</Label>
          <Input id="pw1" type="password" minLength={8} required value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} placeholder="Almeno 8 caratteri" autoComplete="new-password" className="h-11 rounded-xl" data-testid="reset-password-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="pw2">Ripeti password</Label>
          <Input id="pw2" type="password" minLength={8} required value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} autoComplete="new-password" className="h-11 rounded-xl" data-testid="reset-password-confirm-input" /></div>
        <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#1E3A8A] hover:bg-[#172f70]" data-testid="reset-password-submit">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva e accedi
        </Button>
      </form>
    </Shell>
  );
};
